import { getApiBaseUrl } from './apiBase';
const API_BASE_URL = getApiBaseUrl();

export const authService = {
  // Helper: decode JWT payload without verification (base64url)
  _extractRoleFromToken(token) {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) return null;
      const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      // Handle base64 padding
      const padded = base64 + '==='.slice((base64.length + 3) % 4);
      const json = JSON.parse(atob(padded));
      return json?.role || null;
    } catch {
      return null;
    }
  },
  // Sign up with email, password, and optional name
  async signUp(name, email, password, referralCode) {
    try {
      const finalName = (name && name.trim()) || (email ? email.split('@')[0] : 'User');

      // Create user in backend to obtain app token and persisted profile
      const body = { name: finalName, email, password };
      if (referralCode && referralCode.trim()) {
        body.referralCode = referralCode.trim();
      }
      const response = await fetch(`${API_BASE_URL}/auth/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      const ct = response.headers.get('content-type') || '';
      const data = ct.includes('application/json') ? await response.json() : { msg: await response.text() };
      if (!response.ok) {
        return { success: false, error: data.msg || 'Sign up failed' };
      }

      // Store backend token and user
      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify({
        ...data.user,
        name: finalName,
        email: data.user?.email || email,
      }));

      return { success: true, user: data.user };
    } catch (error) {
      console.error('Error signing up:', error);
      return { success: false, error: error?.message || 'An unexpected error occurred.' };
    }
  },
  
  // Sign in with email and password (backend)
  async signIn(email, password) {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });

      const ct = response.headers.get('content-type') || '';
      const data = ct.includes('application/json') ? await response.json() : { msg: await response.text() };

      if (!response.ok) {
        const status = response.status;
        let msg = data.msg || 'Sign in failed';
        if (status === 404) msg = 'User not found';
        else if (status === 401) msg = 'Incorrect password';
        else if (status === 400 && /Invalid credentials/i.test(data.msg || '')) msg = 'Invalid email or password';
        return { success: false, error: msg };
      }

      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
      const roleFromResponse = data?.user?.role;
      const roleFromToken = authService._extractRoleFromToken(data.token);
      const finalRole = roleFromResponse || roleFromToken;
      if (finalRole) {
        localStorage.setItem('role', finalRole);
      }
      return { success: true, user: data.user };
    } catch (error) {
      const offline = typeof navigator !== 'undefined' && navigator.onLine === false;
      if (offline) {
        return { success: false, error: 'No internet connection' };
      }
      console.error('Error signing in:', error);
      return { success: false, error: error?.message || 'Unable to connect. Please try again.' };
    }
  },
  
  

  // Google sign-in (deprecated)
  async signInWithGoogleIdToken(idToken) {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/google`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ idToken }),
      });
      const ct = response.headers.get('content-type') || '';
      const data = ct.includes('application/json') ? await response.json() : { msg: await response.text() };
      if (!response.ok) {
        return { success: false, error: data.msg || 'Google sign-in failed' };
      }
      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
      const roleFromResponse = data?.user?.role;
      const roleFromToken = authService._extractRoleFromToken(data.token);
      const finalRole = roleFromResponse || roleFromToken || 'user';
      localStorage.setItem('role', finalRole);
      return { success: true, user: data.user };
    } catch (error) {
      console.error('Error with Google sign-in:', error);
      return { success: false, error: 'An unexpected error occurred.' };
    }
  },

  async requestPasswordReset(email) {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/request-password-reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const ct = response.headers.get('content-type') || '';
      const data = ct.includes('application/json') ? await response.json() : { msg: await response.text() };
      if (!response.ok) {
        return { success: false, error: data.msg || 'Failed to request reset' };
      }
      return { success: true, msg: data.msg || 'If that email exists, a reset link was sent.' };
    } catch {
      return { success: false, error: 'An unexpected error occurred.' };
    }
  },

  async resetPassword(token, password) {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/reset-password/${encodeURIComponent(token)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      });
      const ct = response.headers.get('content-type') || '';
      const data = ct.includes('application/json') ? await response.json() : { msg: await response.text() };
      if (!response.ok) {
        return { success: false, error: data.msg || 'Failed to reset password' };
      }
      return { success: true, msg: data.msg || 'Password updated successfully' };
    } catch {
      return { success: false, error: 'An unexpected error occurred.' };
    }
  },

  

  // Sign out
  async signOut() {
    try {
      localStorage.removeItem('role');
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('pendingRole');
      return { success: true };
    } catch (error) {
      console.error('Error signing out:', error);
      return { success: false, error: error.message };
    }
  },

  // Get invitation info
  async getInviteInfo(token) {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/invite-info?token=${encodeURIComponent(token)}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await response.json();
      if (!response.ok) {
        return { success: false, error: data.msg || 'Invalid token' };
      }
      return { success: true, email: data.email, role: data.role, centerAddress: data.centerAddress };
    } catch {
      return { success: false, error: 'Connection error' };
    }
  },

  // Register with invitation
  async registerInvite(token, name, password) {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/register-invite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, name, password }),
      });

      const ct = response.headers.get('content-type') || '';
      const data = ct.includes('application/json') ? await response.json() : { msg: await response.text() };

      if (!response.ok) {
        return { success: false, error: data.msg || 'Registration failed' };
      }

      // Store backend token and user
      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify({
        ...data.user,
        name: name,
        email: data.user?.email,
      }));
      
      const roleFromResponse = data?.user?.role;
      const roleFromToken = authService._extractRoleFromToken(data.token);
      const finalRole = roleFromResponse || roleFromToken;
      if (finalRole) {
        localStorage.setItem('role', finalRole);
      }

      return { success: true, user: data.user };
    } catch (error) {
      console.error('Error registering with invite:', error);
      return { success: false, error: error?.message || 'An unexpected error occurred.' };
    }
  }
};
